import { useState, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { CoverImage } from '@/components/CoverImage';
import { Search, BookOpen, BookMarked, Library, Loader2 } from 'lucide-react';
import { BookSearchSkeleton } from './BookSearchSkeleton';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { findOrCreateBook } from '@/lib/bookIdentity';
import { normalizeOpenLibraryId } from '@/lib/searchUtils';
import { useBookSearch } from '@/hooks/useBookSearch';
import type { BookResult } from '@/types/search';

interface Book {
  id: string;
  title: string;
  authors: string[];
  cover_url: string | null;
}

interface BookSelectorProps {
  open: boolean;
  onClose: () => void;
  onSelect: (book: Book | null) => void;
  startWithSearch?: boolean;
  /**
   * Whether the selector should close itself automatically after a book is selected.
   * Defaults to true for standalone usage, but can be disabled when embedded in multi-step flows.
   */
  closeOnSelect?: boolean;
}

export function BookSelector({
  open,
  onClose,
  onSelect,
  startWithSearch = false,
  closeOnSelect = true,
}: BookSelectorProps) {
  const [search, setSearch] = useState('');
  const [showSearch, setShowSearch] = useState(startWithSearch);
  const [searchType, setSearchType] = useState<'title' | 'author'>('title');

  // Use unified book search hook with built-in debounce and request cancellation
  // Must be declared before useEffect that uses clearSearch
  const { results: searchResults, isLoading: isSearching, error, search: performSearch, clear: clearSearch } = useBookSearch({ limit: 20 });
  const isError = !!error;

  // Reset to search mode when dialog opens if startWithSearch is true
  useEffect(() => {
    if (open && startWithSearch) {
      setShowSearch(true);
    } else if (!open) {
      // Reset state when dialog closes
      setSearch('');
      setShowSearch(startWithSearch);
      clearSearch();
    }
  }, [open, startWithSearch, clearSearch]);

  // Fetch currently reading books (skip if starting with search)
  const { data: currentlyReading = [] } = useQuery({
    queryKey: ['user-books-reading'],
    queryFn: async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return [];

      const { data, error } = await supabase
        .from('user_books')
        .select(`
          id,
          book_id,
          books!user_books_book_id_fkey (
            id,
            title,
            authors,
            cover_url
          )
        `)
        .eq('user_id', user.id)
        .eq('shelf', 'reading')
        .order('created_at', { ascending: false });

      if (error) throw error;

      return (data || [])
        .filter(ub => ub.books)
        .map(ub => ub.books as unknown as Book);
    },
    enabled: open && !startWithSearch,
  });

  // Fetch TBR books (skip if starting with search)
  const { data: tbrBooks = [] } = useQuery({
    queryKey: ['user-books-tbr'],
    queryFn: async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return [];

      const { data, error } = await supabase
        .from('user_books')
        .select(`
          id,
          book_id,
          books!user_books_book_id_fkey (
            id,
            title,
            authors,
            cover_url
          )
        `)
        .eq('user_id', user.id)
        .eq('shelf', 'to-read')
        .order('created_at', { ascending: false });

      if (error) throw error;

      return (data || [])
        .filter(ub => ub.books)
        .map(ub => ub.books as unknown as Book);
    },
    enabled: open && !startWithSearch,
  });

  // Trigger search when query changes - hook handles debounce internally
  useEffect(() => {
    if (open && showSearch) {
      performSearch(search, searchType);
    }
  }, [open, showSearch, search, searchType, performSearch]);

  const handleSelect = async (result: any) => {
    try {
      // If it's a local book from edge search
      if (result?.isLocal && result.id) {
        const selected: Book = {
          id: result.id,
          title: result.title || 'Untitled',
          authors: result.authors || (result.author ? [result.author] : []),
          cover_url: result.cover_url || null,
        };
        onSelect(selected);
        if (closeOnSelect) {
          onClose();
        }
        return;
      }

      // If it's already a DB book shape
      if (result?.id && Array.isArray(result.authors)) {
        const selected: Book = {
          id: result.id,
          title: result.title,
          authors: result.authors,
          cover_url: result.cover_url || null,
        };
        onSelect(selected);
        if (closeOnSelect) {
          onClose();
        }
        return;
      }

      // Otherwise, find or create book using ISBN-first pattern
      const bookId = await findOrCreateBook({
        isbn13: result.isbn13,
        isbn10: result.isbn10,
        open_library_id: result.open_library_id,
        title: result.title || 'Untitled',
        subtitle: result.subtitle,
        authors: result.authors || [],
        published_year: result.published_year,
        cover_url: result.cover_url,
        synopsis: result.synopsis,
      });

      const { data: createdBook, error } = await supabase
        .from('books')
        .select('id, title, authors, cover_url')
        .eq('id', bookId)
        .single();

      if (error) throw error;

      const selected: Book = {
        id: createdBook.id,
        title: createdBook.title,
        authors: createdBook.authors || [],
        cover_url: createdBook.cover_url || null,
      };
      onSelect(selected);
      if (closeOnSelect) {
        onClose();
      }
    } catch (e) {
      console.error('Select book failed', e);
    }
  };
  const handleNoBook = () => {
    onSelect(null);
    onClose();
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl p-0">
        <div className="flex max-h-[80vh] flex-col">
          <DialogHeader className="px-6 pt-6">
            <DialogTitle className="font-heading text-2xl">
              {startWithSearch ? 'Select a Book' : 'Choose a Book to Focus On'}
            </DialogTitle>
            <DialogDescription className="sr-only">
              {startWithSearch ? 'Search for any book' : 'Select a book from your lists or search for any book'}
            </DialogDescription>
          </DialogHeader>

          <div 
            className="flex-1 overflow-y-auto px-6 pb-6 touch-pan-y" 
            style={{ WebkitOverflowScrolling: 'touch', overscrollBehavior: 'contain' }}
          >
            <div className="space-y-4">
          {!showSearch ? (
            <>
              {/* Search Button - at top for discoverability */}
              <div className="pb-2">
                <Button
                  onClick={() => setShowSearch(true)}
                  variant="outline"
                  className="w-full flex items-center gap-2"
                >
                  <Library className="w-4 h-4" />
                  Search for Any Book
                </Button>
              </div>

              {/* Currently Reading Section */}
              {currentlyReading.length > 0 && (
                <div className="space-y-3">
                  <div className="flex items-center gap-2 text-sm font-medium text-primary">
                    <BookOpen className="w-4 h-4" />
                    <span>Currently Reading</span>
                  </div>
                  <div className="grid gap-2">
                    {currentlyReading.map((book) => (
                      <button
                        key={book.id}
                        onClick={() => handleSelect(book)}
                        className="flex items-center gap-4 p-3 rounded-lg hover:bg-primary/10 border-2 border-primary/20 transition-colors text-left"
                      >
                        <CoverImage
                          bookId={book.id}
                          title={book.title}
                          author={book.authors?.[0]}
                          coverUrl={book.cover_url}
                          size="xs"
                        />
                        <div className="flex-1 min-w-0">
                          <h4 className="font-semibold text-foreground line-clamp-2 break-words">
                            {book.title}
                          </h4>
                          <p className="text-sm text-muted-foreground line-clamp-1 break-words">
                            {book.authors?.join(', ') || 'Unknown Author'}
                          </p>
                        </div>
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {/* TBR Section */}
              {tbrBooks.length > 0 && (
                <div className="space-y-3">
                  <div className="flex items-center gap-2 text-sm font-medium text-muted-foreground">
                    <BookMarked className="w-4 h-4" />
                    <span>Your TBR List</span>
                  </div>
                  <div className="grid gap-2">
                    {tbrBooks.map((book) => (
                      <button
                        key={book.id}
                        onClick={() => handleSelect(book)}
                        className="flex items-center gap-4 p-3 rounded-lg hover:bg-muted transition-colors text-left"
                      >
                        <CoverImage
                          bookId={book.id}
                          title={book.title}
                          author={book.authors?.[0]}
                          coverUrl={book.cover_url}
                          size="xs"
                        />
                        <div className="flex-1 min-w-0">
                          <h4 className="font-semibold text-foreground line-clamp-2 break-words">
                            {book.title}
                          </h4>
                          <p className="text-sm text-muted-foreground line-clamp-1 break-words">
                            {book.authors?.join(', ') || 'Unknown Author'}
                          </p>
                        </div>
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {/* Empty States */}
              {currentlyReading.length === 0 && tbrBooks.length === 0 && (
                <div className="text-center py-8 space-y-3">
                  <BookOpen className="w-12 h-12 mx-auto text-muted-foreground" />
                  <div>
                    <p className="font-medium text-foreground">No books in your reading list</p>
                    <p className="text-sm text-muted-foreground">
                      Add books to your shelves or search for any book
                    </p>
                  </div>
                </div>
              )}
            </>
          ) : (
            <>
              {/* Search Mode */}
              <div className="space-y-4">
                <div className="flex items-center gap-2">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => {
                      setShowSearch(false);
                      setSearch('');
                    }}
                  >
                    ← Back
                  </Button>
                </div>

                <div className="space-y-3">
                  <div className="flex gap-2">
                    <Button
                      variant={searchType === 'title' ? 'default' : 'outline'}
                      size="sm"
                      onClick={() => setSearchType('title')}
                      className="flex-1"
                    >
                      Title
                    </Button>
                    <Button
                      variant={searchType === 'author' ? 'default' : 'outline'}
                      size="sm"
                      onClick={() => setSearchType('author')}
                      className="flex-1"
                    >
                      Author
                    </Button>
                  </div>

                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                    <Input
                      placeholder={searchType === 'title' ? "Search by book title..." : "Search by author name..."}
                      value={search}
                      onChange={(e) => setSearch(e.target.value)}
                      className="pl-10"
                      autoFocus
                    />
                  </div>
                </div>

                <div>
                  {isError ? (
                    <Alert variant="destructive" className="my-4">
                      <AlertDescription>
                        {error || 'Search failed. Please try again.'}
                      </AlertDescription>
                    </Alert>
                  ) : isSearching ? (
                    <BookSearchSkeleton count={5} compact />
                  ) : search.length < 2 ? (
                    <div className="text-center py-12 text-muted-foreground">
                      Type at least 2 characters to search
                    </div>
                  ) : searchResults.length === 0 ? (
                    <div className="text-center py-12 space-y-3">
                      <BookOpen className="w-12 h-12 mx-auto text-muted-foreground" />
                      <p className="text-muted-foreground">
                        No books found matching "{search}"
                      </p>
                    </div>
                  ) : (
                    <div className="grid gap-3">
                      {searchResults.map((book, index) => (
                        <button
                          key={(book as any).id || (book as any).open_library_id || index}
                          onClick={() => handleSelect(book)}
                          className="flex items-center gap-4 p-3 rounded-lg hover:bg-muted transition-colors text-left"
                        >
                          <CoverImage
                            bookId={(book as any).id}
                            isbn13={(book as any).isbn13}
                            isbn10={(book as any).isbn10}
                            olid={normalizeOpenLibraryId((book as any).open_library_id || '')}
                            coverUrl={(book as any).cover_url}
                            title={book.title}
                            author={(book as any).authors?.[0]}
                            size="xs"
                          />
                          <div className="flex-1 min-w-0">
                              <h4 className="font-semibold text-foreground line-clamp-2 break-words">
                                {book.title}
                              </h4>
                              <p className="text-sm text-muted-foreground line-clamp-1 break-words">
                                {(book as any).authors?.join(', ') || 'Unknown Author'}
                              </p>
                          </div>
                        </button>
                      ))}
                      </div>
                    )}
                </div>
              </div>
            </>
          )}
            </div>
          </div>
          
          {/* No book option - sticky footer (only for focus mode) */}
          {!startWithSearch && (
            <div className="border-t p-4">
              <Button
                onClick={handleNoBook}
                variant="outline"
                className="w-full btn-pill"
              >
                Continue without selecting a book
              </Button>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
